import { Component } from '@angular/core';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { distinctUntilChanged } from 'rxjs/operators';
import { ComparisonUtility } from '@utilities/comparison.utility';
import { ProcessingManager } from '@managers/processing.manager';
import { TableLikeBaseComponent } from '../../../table-like-base.component';
import { Server } from '@models/ui/server.model';
import { DateUtility } from '@utilities/date.utility';
import { ServerService } from '@services/server.service';
import { ServerState } from '@models/servers/server-state.model';
import { Sort } from '@angular/material/sort';
import { orderBy } from "lodash-es";
import { Clipboard } from '@angular/cdk/clipboard';
import { NotificationManager } from '@managers/notification.manager';
import { UserConfigsService } from "@services/user-configs.service";
import { Role } from "@models/role.model";
import { rowTransitionAnimation } from "../animations";

@Component({
    selector: 'lex-all-steps',
    templateUrl: './servers-table.component.html',
    styleUrls: ['./servers-table.component.scss'],
    animations: [rowTransitionAnimation],
    standalone: false
})
export class ServersTableComponent extends TableLikeBaseComponent<Server> {
	public dataSource$: Observable<MatTableDataSource<Server>>;
	public rowHeight$ = this.processingManager.currentRowHeight$;
	public hideTerminatedServers$ = new BehaviorSubject<boolean>(true);
	public sort$ = new BehaviorSubject<Sort>(null);
	public columnsToDisplay = [
		'id',
		'ip',
		'version',
		'memory',
		'lastPing',
		'type',
		'state',
		'lastStateChange'
	];

	constructor(
		public processingManager: ProcessingManager,
		public serverService: ServerService,
		public clipboard: Clipboard,
		public notificationManager: NotificationManager,
		private userConfigsService: UserConfigsService,
		private dateUtility: DateUtility,
	) {
		super();

		this.dataSource$ = combineLatest([
			this.processingManager.servers$,
			this.processingManager.currentFilterText$,
			this.hideTerminatedServers$,
			this.sort$
		]).pipe(
			filter(([collection]) => !!collection),
			distinctUntilChanged((a, b) => ComparisonUtility.isEqual(a, b)),
			map(([serversCollection, term, hideTerminated, sortOptions]) => {
				if (term) {
					serversCollection = serversCollection?.filter((item) => {
						return item.instanceId?.toLowerCase().includes(term.toLowerCase()) ||
							item.serverState?.displayName?.toLowerCase().includes(term.toLowerCase()) ||
							item.serverState?.value?.toLowerCase().includes(term.toLowerCase()) ||
							item.serverIP?.toLowerCase().includes(term.toLowerCase()) ||
							item.serverType?.displayName.toLowerCase().includes(term.toLowerCase()) ||
							item.serverType?.value.toLowerCase().includes(term.toLowerCase());
					});
				}

				if (hideTerminated) {
					serversCollection = serversCollection?.filter((item) => {
						return item.serverState.value !== ServerState.Terminated.value;
					});
				}

				if (sortOptions) {
					serversCollection = orderBy(serversCollection, sortOptions.active, sortOptions.direction as 'asc' | 'desc');
				}

				if (this.userConfigsService.inRoles(Role.Administrator) && !this.columnsToDisplay.includes('controls')) {
					this.columnsToDisplay.unshift('controls');
				}

				return new MatTableDataSource(serversCollection as any);
			})
		);
	}

	public trackByFn(index: number, item: any) {
		return `${item.caseId}:${item.functionName}:${item.functionCount}`;
	}

	public isPingOld(server: Server) {
		const diff = this.dateUtility.diff(new Date(server.lastPing), new Date());
		return diff.minutes > 20;
	}

	public toggleState(server: Server) {
		this.serverService.toggleServer(server.serverId).subscribe();
	}

	public startAll(): void {
		this.serverService.transitionAll(ServerState.Idle.value).subscribe();
	}

	public stopAll(): void {
		this.serverService.transitionAll(ServerState.Stopping.value).subscribe();
	}

	public setHideTerminated(value: boolean): void {
		this.hideTerminatedServers$.next(value);
	}

	public sort(sort: Sort): void {
		this.sort$.next(sort);
	}

	public copyTextToClipboard(text: string) {
		this.clipboard.copy(text);
		this.notificationManager.displaySnackBarMessage({
			message: 'Copied to clipboard!'
		});
	}
}
